import { useEffect } from "react";
import { useBranding } from "./branding.js";

/**
 * The browser tab, kept honest about which of the app's 22 routes is open.
 *
 * Before this, every page left the tab reading the workspace name alone —
 * fine with one tab open, useless with the ten a real workday accumulates.
 * Chat is the sharpest case: every conversation is the same URL shape
 * (`/chat/:channelId`, a bare id — see main.tsx), so without a page title
 * the tab strip cannot tell one open DM from another at all.
 *
 * Takes a plain string, not the `ReactNode` `PageBody`/`PageHeader` accept
 * for their visible heading — those routinely carry an avatar, a loading
 * skeleton, or a `<select>` (DailyReportPerson's person switcher), none of
 * which is valid tab-title text. A route with a JSX heading calls this
 * separately with the plain-text equivalent.
 *
 * Skipped entirely while `name` is empty — a still-loading detail page
 * would otherwise flash "Astrum Connect" over whatever the previous page
 * had set, then correct itself a moment later once data arrives.
 */
export function usePageTitle(name: string): void {
  const { workspaceName } = useBranding();

  useEffect(() => {
    if (!name) return;
    const previous = document.title;
    document.title = `${name} · ${workspaceName}`;
    return () => {
      document.title = previous;
    };
  }, [name, workspaceName]);
}
